import { getApiBaseUrl, makeApiCall as makeStandardApiCall } from './ApiUtils';
import PdfTextExtractionService from './PdfTextExtractionService';
import VideoAnalysisService from './VideoAnalysisService';
// AI Evaluation Service
// Updated to call backend API instead of OpenAI directly
// Preserves all complex evaluation logic and prompts




// Helper function to analyze image content using backend API
const analyzeImageWithOpenAI = async (imageUrl, questionContext) => {
  try {
    const result = await makeStandardApiCall('/api/analyze-image', {
      imageUrl,
      questionContext
    });

    return result.analysis;
  } catch (error) {
    console.error('Image analysis failed:', error);
    return `Image analysis failed: ${error.message}`;
  }
};

// Helper function to evaluate video scenario using backend API
const evaluateVideoScenarioWithOpenAI = async (question, answer, videoAnalysis, maxScore) => {
  try {
    const result = await makeStandardApiCall('/api/evaluate-video-scenario', {
      question,
      answer,
      videoAnalysis,
      maxScore
    });

    return result;
  } catch (error) {
    console.error('OpenAI evaluation failed:', error);
    // Fallback to direct evaluation
    return evaluateVideoScenarioDirect(question, answer, videoAnalysis);
  }
};

// Helper function to extract video transcript from answer if present
// Similar to PDF extraction - extracts transcript during AI evaluation
// Logs extracted transcript to file (not saved to database to save costs)
const extractVideoTranscriptFromAnswer = async (answer, questionId, userEmail, examCode) => {
  try {
    // Check if answer has a video
    if (answer && typeof answer === 'object' && (answer.videoUrl || answer.videoPath)) {
      const videoUrlOrPath = answer.videoUrl || answer.videoPath;
      
      try {
        // Use VideoAnalysisService to transcribe (it uses the new Google Speech-to-Text endpoint)
        const transcriptionResult = await VideoAnalysisService.transcribeVideo(videoUrlOrPath);
        
        if (transcriptionResult.success && transcriptionResult.transcript && transcriptionResult.transcript.trim()) {
          const transcript = transcriptionResult.transcript;
          
          // Log extracted transcript to file (similar to PDF extraction)
          await logVideoTranscriptionToFile({
            userEmail: userEmail || 'unknown',
            questionId: questionId || 'unknown',
            examCode: examCode || 'unknown',
            videoUrl: videoUrlOrPath,
            transcript: transcript,
            transcriptLength: transcript.length,
            wordCount: transcript.split(' ').length,
            confidence: transcriptionResult.confidence || 0,
            extractedAt: transcriptionResult.timestamp || new Date().toISOString(),
            error: null
          });
          
          return {
            transcript: transcript,
            confidence: transcriptionResult.confidence || 0,
            wordCount: transcriptionResult.wordCount || 0,
            words: transcriptionResult.words || []
          };
        } else {
          const errorMsg = transcriptionResult.error || 'Transcription failed';
          
          // Log error to file
          await logVideoTranscriptionToFile({
            userEmail: userEmail || 'unknown',
            questionId: questionId || 'unknown',
            examCode: examCode || 'unknown',
            videoUrl: videoUrlOrPath,
            transcript: '',
            transcriptLength: 0,
            wordCount: 0,
            confidence: 0,
            extractedAt: new Date().toISOString(),
            error: errorMsg
          });
          
          return null;
        }
      } catch (transcriptionError) {
        // Log error to file
        await logVideoTranscriptionToFile({
          userEmail: userEmail || 'unknown',
          questionId: questionId || 'unknown',
          examCode: examCode || 'unknown',
          videoUrl: videoUrlOrPath,
          transcript: '',
          transcriptLength: 0,
          wordCount: 0,
          confidence: 0,
          extractedAt: new Date().toISOString(),
          error: transcriptionError.message
        });
        
        return null;
      }
    }
    return null;
  } catch (error) {
    console.error('Video transcription error:', error);
    return null;
  }
};

// Helper function to log video transcription to file (similar to PDF extraction logging)
const logVideoTranscriptionToFile = async (data) => {
  try {
    // This would log to a file similar to PDF extraction
    // For now, just log to console - you can implement file logging if needed
    console.log(`📝 Video Transcription Log:`, {
      userEmail: data.userEmail,
      questionId: data.questionId,
      examCode: data.examCode,
      transcriptLength: data.transcriptLength,
      wordCount: data.wordCount,
      confidence: data.confidence,
      error: data.error
    });
    
    // TODO: Implement file logging similar to PDF extraction if needed
    // You can use the same pattern as logPdfExtractionToFile
  } catch (error) {
    console.error('Failed to log video transcription:', error);
  }
};

// Helper function to extract PDF text from answer if present
// Logs extracted text to file (not saved to database to save costs)
const extractPdfTextFromAnswer = async (answer, questionId, userEmail) => {
  try {
    // Check if answer has a PDF file
    if (answer && typeof answer === 'object' && answer.pdfFile) {
      const pdfFileName = answer.pdfFile.fileName || answer.pdfFile.originalFileName || 'unknown.pdf';
      console.log(`📄 [Step 1] PDF Found: ${pdfFileName} for question ${questionId}`);
      
      console.log(`📄 [Step 2] Extracting text from PDF...`);
      const pdfExtractionResult = await PdfTextExtractionService.extractText(answer);
      
      if (pdfExtractionResult.text && pdfExtractionResult.text.trim()) {
        console.log(`📄 [Step 2] ✅ PDF Text Extracted: ${pdfExtractionResult.text.length} characters`);
        
        // Log extracted text to file (not database to save costs)
        await logPdfExtractionToFile({
          userEmail: userEmail || 'unknown',
          questionId: questionId || 'unknown',
          pdfFileName: pdfFileName,
          extractedText: pdfExtractionResult.text,
          textLength: pdfExtractionResult.text.length,
          extractionMethod: pdfExtractionResult.extractionMethod || 'pdf-parse',
          metadata: pdfExtractionResult.metadata || {},
          extractedAt: pdfExtractionResult.extractedAt || new Date().toISOString(),
          error: pdfExtractionResult.error || null
        });
        
        return pdfExtractionResult.text;
      } else if (pdfExtractionResult.error) {
        console.log(`📄 [Step 2] ❌ PDF Extraction Failed: ${pdfExtractionResult.error}`);
        // Log error to file
        await logPdfExtractionToFile({
          userEmail: userEmail || 'unknown',
          questionId: questionId || 'unknown',
          pdfFileName: pdfFileName,
          extractedText: '',
          textLength: 0,
          extractionMethod: pdfExtractionResult.extractionMethod || 'pdf-parse',
          metadata: {},
          extractedAt: new Date().toISOString(),
          error: pdfExtractionResult.error
        });
      }
    } else {
      console.log(`📄 [Step 1] No PDF found for question ${questionId}`);
    }
    return null;
  } catch (error) {
    console.error(`📄 [Step 2] ❌ PDF Extraction Error:`, error.message);
    
    // Log error to file
    if (answer && typeof answer === 'object' && answer.pdfFile) {
      const pdfFileName = answer.pdfFile.fileName || answer.pdfFile.originalFileName || 'unknown.pdf';
      await logPdfExtractionToFile({
        userEmail: userEmail || 'unknown',
        questionId: questionId || 'unknown',
        pdfFileName: pdfFileName,
        extractedText: '',
        textLength: 0,
        extractionMethod: 'pdf-parse',
        metadata: {},
        extractedAt: new Date().toISOString(),
        error: error.message
      });
    }
    
    return null;
  }
};

// Helper function to log PDF extraction to file via backend API
const logPdfExtractionToFile = async (logData) => {
  try {
    const { makeApiCall } = await import('./ApiUtils');
    await makeApiCall('/api/log-pdf-extraction', logData);
  } catch (error) {
    console.error('Failed to log PDF extraction to file:', error);
    // Don't throw - logging failure shouldn't break evaluation
  }
};

// Helper function to evaluate scenario text using backend API (complex evaluation with criteria)
const evaluateScenarioTextWithOpenAI = async (question, answer, images, maxScore) => {
  try {
    // Extract PDF text if present and combine with answer text
    let enrichedAnswer = { ...answer };
    const pdfText = await extractPdfTextFromAnswer(answer, question?.id, answer?.userEmail);
    
    if (pdfText) {
      // Combine PDF text with existing answer text
      const existingText = answer.text || answer.answer || '';
      enrichedAnswer.text = PdfTextExtractionService.combineTextWithPdf(existingText, pdfText);
    }
    
    const result = await makeStandardApiCall('/api/evaluate-scenario-text', {
      question,
      answer: enrichedAnswer,
      images,
      maxScore
    });

    return result;
  } catch (error) {
    console.error('AI evaluation failed:', error);
    // Fallback to direct evaluation
    return evaluateScenarioTextDirect(question, answer);
  }
};

// Helper function to get evaluation points from question (combines evaluationKeywords and evaluationPoints)
const getEvaluationPoints = (question, evaluationPointsParam = null) => {
  // Priority: parameter > evaluationPoints > evaluationKeywords
  let points = evaluationPointsParam;
  
  if (!points) {
    // Check both evaluationPoints and evaluationKeywords (for backward compatibility)
    const evalPoints = question.evaluationPoints;
    const evalKeywords = question.evaluationKeywords;
    
    // Combine both if they exist
    if (evalPoints && evalKeywords) {
      // Both exist - combine them
      const pointsArray = typeof evalPoints === 'string' 
        ? evalPoints.split(',').map(p => p.trim()).filter(Boolean)
        : Array.isArray(evalPoints) ? evalPoints : [];
      const keywordsArray = typeof evalKeywords === 'string'
        ? evalKeywords.split(',').map(k => k.trim()).filter(Boolean)
        : Array.isArray(evalKeywords) ? evalKeywords : [];
      
      // Combine and remove duplicates
      const combined = [...pointsArray, ...keywordsArray];
      points = [...new Set(combined)]; // Remove duplicates
    } else if (evalPoints) {
      points = evalPoints;
    } else if (evalKeywords) {
      points = evalKeywords;
    } else {
      points = [];
    }
  }
  
  // Convert to array if needed
  if (typeof points === 'string' && points.trim()) {
    points = points.split(',').map(p => p.trim()).filter(Boolean);
  } else if (!Array.isArray(points)) {
    points = [];
  }
  
  return points;
};

// Helper function to evaluate text using backend API
// NOTE: PDF/video extraction should be done BEFORE calling this method
// This method expects the answer.text to already contain the extracted content
const evaluateTextWithOpenAI = async (question, answer, images, maxScore, evaluationPoints = null) => {
  try {
    // Use answer as-is (PDF/video text should already be extracted and combined)
    const enrichedAnswer = { ...answer };
    
    // Get evaluation points (combines evaluationKeywords and evaluationPoints)
    let points = getEvaluationPoints(question, evaluationPoints);
    
    const result = await makeStandardApiCall('/api/evaluate-text', {
      question,
      answer: enrichedAnswer,
      images,
      maxScore,
      evaluationPoints: points // Pass evaluation points to API
    });

    return result;
  } catch (error) {
    console.error('AI evaluation failed:', error);
    return {
      score: 0,
      maxScore: maxScore || question.maxScore || question.marks || 10,
      feedback: "Text evaluation failed",
      evaluationType: "text",
      confidence: 0,
      textScore: 0,
      imageScore: 0,
      imageAnalysis: "Evaluation failed",
      hasImages: images ? images.length > 0 : false,
      imageCount: images ? images.length : 0
    };
  }
};

// Helper function to evaluate code using backend API
const evaluateCodeWithOpenAI = async (question, answer, maxScore) => {
  try {
    console.log(`🤖 [Question ${question?.id}] Starting evaluation (type: code)`);
    
    // Extract PDF text if present and combine with answer text
    let enrichedAnswer = { ...answer };
    const pdfText = await extractPdfTextFromAnswer(answer, question?.id, answer?.userEmail);
    
    if (pdfText) {
      // Combine PDF text with existing answer text
      const existingText = answer.text || answer.answer || answer.code || '';
      enrichedAnswer.text = PdfTextExtractionService.combineTextWithPdf(existingText, pdfText);
      console.log(`🤖 [Question ${question?.id}] Combined text: ${enrichedAnswer.text.length} chars (original: ${existingText.length}, PDF: ${pdfText.length})`);
    }
    
    console.log(`🤖 [Question ${question?.id}] Step 3: Calling AI evaluation API...`);
    const result = await makeStandardApiCall('/api/evaluate-code', {
      question,
      answer: enrichedAnswer,
      maxScore
    });

    return result;
  } catch (error) {
    console.error('AI evaluation failed:', error);
    return {
      score: 0,
      maxScore: maxScore || question.maxScore || question.marks || 10,
      feedback: "Code evaluation failed",
      codeReview: "Evaluation service unavailable"
    };
  }
};

// Direct evaluation for scenario-text (if AI not enabled)
const evaluateVideoScenarioDirect = async (question, answer, videoAnalysis) => {
  const maxScore = question.maxScore || question.marks || 10;
  
  // Extract video analysis data
  const transcript = videoAnalysis?.transcript || '';
  const selectedScenario = answer?.selectedScenario || '';
  
  if (!transcript.trim() || !selectedScenario) {
    return {
      score: 0,
      maxScore,
      feedback: "No transcript or scenario provided",
      evaluationType: "video-scenario",
      confidence: 0,
      selectedScenario,
      matchedConcepts: [],
      transcript: transcript.substring(0, 200) + (transcript.length > 200 ? '...' : '')
    };
  }

  // Simple keyword matching as fallback
  const scenarioKeywords = selectedScenario.toLowerCase().split(' ');
  const transcriptLower = transcript.toLowerCase();
  
  let matchedKeywords = [];
  let score = 0;
  
  scenarioKeywords.forEach(keyword => {
    if (transcriptLower.includes(keyword)) {
      matchedKeywords.push(keyword);
      score += (maxScore / scenarioKeywords.length);
    }
  });

  return {
    score: Math.min(maxScore, Math.max(0, Math.round(score))),
    maxScore,
    feedback: `Direct evaluation: Found ${matchedKeywords.length} matching keywords: ${matchedKeywords.join(', ')}`,
    evaluationType: "video-scenario",
    confidence: 0.5,
    selectedScenario,
    matchedConcepts: matchedKeywords,
    transcript: transcript.substring(0, 200) + (transcript.length > 200 ? '...' : '')
  };
};

// Direct evaluation for scenario-text (fallback)
const evaluateScenarioTextDirect = async (question, answer) => {
  const maxScore = question.maxScore || question.marks || 10;
  const selectedScenario = answer?.selectedScenario || '';
  
  // Extract PDF text if present
  let pdfText = null;
  try {
    pdfText = await extractPdfTextFromAnswer(answer, question?.id, answer?.userEmail);
  } catch (error) {
    console.warn('PDF extraction failed in direct evaluation:', error);
  }
  
  // Extract text content from answer
  let textContent = '';
  if (answer.text) {
    textContent = extractTextFromHTML(answer.text);
  } else if (answer.answer) {
    textContent = extractTextFromHTML(answer.answer);
  } else if (typeof answer === 'string') {
    textContent = extractTextFromHTML(answer);
  }
  
  // Combine with PDF text if available
  if (pdfText) {
    textContent = PdfTextExtractionService.combineTextWithPdf(textContent, pdfText);
  }

  if (!textContent.trim()) {
    return {
      score: 0,
      maxScore,
      feedback: "No answer provided",
      evaluationType: "scenario-text",
      confidence: 0
    };
  }

  // Find the selected scenario and its evaluation points
  const scenarioOption = question.scenarioOptions?.find(opt => opt.title === selectedScenario);
  const evaluationPoints = scenarioOption?.evaluationPoints || [];
  
  let criteriaScore = 0;
  let criteriaBreakdown = {};
  
  if (evaluationPoints.length > 0) {
    let criteriaFound = 0;
    const scorePerPoint = maxScore / evaluationPoints.length;
    const textContentLower = textContent.toLowerCase();
    
    evaluationPoints.forEach((point, index) => {
      const pointLower = point.toLowerCase();
      const isFound = textContentLower.includes(pointLower);
      const pointScore = isFound ? scorePerPoint : 0;
      
      if (isFound) {
        criteriaFound++;
      }
      
      criteriaBreakdown[`criterion${index + 1}`] = {
        criterion: point,
        score: Math.round(pointScore * 10) / 10,
        maxScore: Math.round(scorePerPoint * 10) / 10,
        found: isFound,
        evaluationKey: point,
        feedback: isFound 
          ? `✓ EVALUATION KEY: "${point}" - FOUND in response ✓ Marks: ${Math.round(pointScore * 10) / 10}/${Math.round(scorePerPoint * 10) / 10} - REASON: Criterion was successfully addressed` 
          : `✗ EVALUATION KEY: "${point}" - NOT FOUND in response ✗ Marks: 0/${Math.round(scorePerPoint * 10) / 10} - REASON: Criterion was not addressed in the response`
      };
    });
    
    criteriaScore = criteriaFound * scorePerPoint;
  }
  
  // Generate detailed feedback
  let feedback = `📊 MARKS-BASED EVALUATION BREAKDOWN:\n`;
  feedback += `�� EVALUATION CRITERIA: ${Math.round(criteriaScore * 10) / 10}/${maxScore} marks (${evaluationPoints.length} criteria × ${Math.round((maxScore / evaluationPoints.length) * 10) / 10} marks each)\n`;
  feedback += `🏆 TOTAL SCORE: ${Math.round(criteriaScore * 10) / 10}/${maxScore} marks`;
  
  return {
    score: Math.round(criteriaScore * 10) / 10,
    maxScore,
    feedback: feedback,
    evaluationType: "scenario-text",
    confidence: 0.5,
    selectedScenario,
    criteriaBreakdown
  };
};

// Helper function to extract plain text from HTML content
const extractTextFromHTML = (htmlContent) => {
  if (!htmlContent) return '';
  
  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlContent;
  
  // Extract text content, removing extra whitespace
  const textContent = tempDiv.textContent || tempDiv.innerText || '';
  return textContent.replace(/\s+/g, ' ').trim();
};

// Helper function for basic syntax checking
const checkBasicSyntax = (code) => {
  const commonErrors = [
    /[^;{}]\s*$/, // Missing semicolon or brace
    /\(\s*\)/, // Empty parentheses
    /\[\s*\]/, // Empty brackets
    /{\s*}/, // Empty braces
  ];
  
  return commonErrors.some(pattern => pattern.test(code));
};

// Helper function to get file type from URL
const getFileTypeFromUrl = (url) => {
  const extension = url.split('.').pop().toLowerCase();
  return extension;
};

// Direct evaluation for text (fallback)
const evaluateTextDirect = async (question, answer) => {
  const maxScore = question.maxScore || question.marks || 10;
  
  // Extract PDF text if present
  let pdfText = null;
  try {
    pdfText = await extractPdfTextFromAnswer(answer, question?.id, answer?.userEmail);
  } catch (error) {
    console.warn('PDF extraction failed in direct evaluation:', error);
  }
  
  // Extract text content from answer
  let textContent = '';
  if (answer.text) {
    textContent = extractTextFromHTML(answer.text);
  } else if (answer.answer) {
    textContent = extractTextFromHTML(answer.answer);
  } else if (typeof answer === 'string') {
    textContent = extractTextFromHTML(answer);
  }
  
  // Combine with PDF text if available
  if (pdfText) {
    textContent = PdfTextExtractionService.combineTextWithPdf(textContent, pdfText);
  }

  if (!textContent.trim()) {
    return {
      score: 0,
      maxScore,
      feedback: "No answer provided",
      evaluationType: "text",
      confidence: 0
    };
  }

  // Simple length-based scoring as fallback
  const wordCount = textContent.split(/\s+/).length;
  const score = Math.min(maxScore, Math.max(0, Math.round((wordCount / 50) * maxScore)));

  return {
    score,
    maxScore,
    feedback: `Direct evaluation: ${wordCount} words provided. Score based on response length.`,
    evaluationType: "text",
    confidence: 0.3,
    textScore: score,
    imageScore: 0,
    imageAnalysis: "No images provided",
    hasImages: false,
    imageCount: 0
  };
};

// Direct evaluation for code (fallback)
const evaluateCodeDirect = async (question, answer) => {
  const maxScore = question.maxScore || question.marks || 10;
  
  // Extract code content from answer
  let codeContent = '';
  if (answer.text) {
    codeContent = answer.text;
  } else if (answer.answer) {
    codeContent = answer.answer;
  } else if (typeof answer === 'string') {
    codeContent = answer;
  }

  if (!codeContent.trim()) {
    return {
      score: 0,
      maxScore,
      feedback: "No code provided",
      codeReview: "No code to review"
    };
  }

  // Basic syntax checking
  const hasSyntaxErrors = checkBasicSyntax(codeContent);
  const lineCount = codeContent.split('\n').length;
  
  // Simple scoring based on code length and syntax
  let score = Math.min(maxScore, Math.max(0, Math.round((lineCount / 20) * maxScore)));
  if (hasSyntaxErrors) {
    score = Math.max(0, score - (maxScore * 0.2));
  }

  return {
    score,
    maxScore,
    feedback: `Direct evaluation: ${lineCount} lines of code. ${hasSyntaxErrors ? 'Some syntax issues detected.' : 'No obvious syntax errors.'}`,
    codeReview: hasSyntaxErrors ? "Code has some syntax issues that need attention." : "Code appears to have basic structure."
  };
};

// Helper function to calculate MCQ score based on individual option marks
const calculateMCQScore = (question, answer) => {
  let score = 0;
  
  // Add null check for options
  if (!question.options || !Array.isArray(question.options)) {
    console.warn(`Question ${question.id} has no options array`);
    return 0;
  }
  
  if (question.type === 'mcq-single' && answer) {
    const selectedOption = question.options.find(opt => opt.id === answer);
    if (selectedOption && selectedOption.isCorrect) {
      score = Number(selectedOption.marks) || 1;
    }
  } else if (question.type === 'mcq-multi') {
    if (Array.isArray(answer) && answer.length > 0) {
      answer.forEach(optionId => {
        const selectedOption = question.options.find(opt => opt.id === optionId);
        if (selectedOption && selectedOption.isCorrect) {
          score += Number(selectedOption.marks) || 1;
        }
      });
    } else if (typeof answer === 'string' && answer) {
      const selectedOption = question.options.find(opt => opt.id === answer);
      if (selectedOption && selectedOption.isCorrect) {
        score += Number(selectedOption.marks) || 1;
      }
    }
  }
  
  return score;
};

// Helper function to get selected options for MCQ
const getSelectedOptions = (question, answer) => {
  // Add null check for options
  if (!question.options || !Array.isArray(question.options)) {
    return [];
  }
  
  if (question.type === 'mcq-single' && answer) {
    const selectedOption = question.options.find(opt => opt.id === answer);
    return selectedOption ? [selectedOption.text] : [];
  } else if (question.type === 'mcq-multi') {
    if (Array.isArray(answer) && answer.length > 0) {
      const selectedOpts = question.options.filter(opt => answer.includes(opt.id));
      return selectedOpts.map(opt => opt.text);
    } else if (typeof answer === 'string' && answer) {
      const selectedOption = question.options.find(opt => opt.id === answer);
      return selectedOption ? [selectedOption.text] : [];
    }
  }
  return [];
};

// Main evaluation functions (keeping the same interface as original)
export const evaluateQuestion = async (question, answer, videoAnalysis = null, images = []) => {
  const maxScore = question.maxScore || question.marks || 10;
  
  // Handle MCQ questions with direct evaluation (no OpenAI needed)
  if (question.type === 'mcq-single' || question.type === 'mcq-multi') {
    const mcqScore = calculateMCQScore(question, answer);
    const options = question.options || [];
    return {
      score: mcqScore,
      maxScore,
      feedback: "MCQ score calculated based on individual option marks",
      evaluationType: "mcq",
      confidence: 100,
      selectedOptions: getSelectedOptions(question, answer),
      correctOptions: options.filter(opt => opt.isCorrect).map(opt => opt.text),
      totalOptions: options.length,
      questionType: question.type
    };
  }
  
  // Determine question type and call appropriate evaluation
  if (question.type === 'video-scenario' && videoAnalysis) {
    return await evaluateVideoScenarioWithOpenAI(question, answer, videoAnalysis, maxScore);
  } else if (question.type === 'scenario-text') {
    return await evaluateScenarioTextWithOpenAI(question, answer, images, maxScore);
  } else if (question.type === 'code' || question.subType === 'code') {
    return await evaluateCodeWithOpenAI(question, answer, maxScore);
  } else if (question.type === 'text') {
    // New "text" type question with PDF - extract PDF text first
    let enrichedAnswer = { ...answer };
    
    // Extract PDF text if present
    const pdfText = await extractPdfTextFromAnswer(answer, question?.id, answer?.userEmail);
    if (pdfText) {
      // Combine PDF text with existing answer text
      const existingText = answer.text || answer.answer || '';
      enrichedAnswer.text = PdfTextExtractionService.combineTextWithPdf(existingText, pdfText);
    }
    
    // Call evaluateTextWithOpenAI with evaluation points (combines evaluationKeywords and evaluationPoints)
    return await evaluateTextWithOpenAI(question, enrichedAnswer, images, maxScore, getEvaluationPoints(question));
  } else if (question.type === 'video') {
    // New "video" type question - extract video transcript first
    let enrichedAnswer = { ...answer };
    
    // Extract video transcript if not already available
    let transcript = null;
    if (videoAnalysis && videoAnalysis.transcript) {
      transcript = videoAnalysis.transcript;
    } else if (answer.transcript) {
      transcript = answer.transcript;
    } else if (answer.videoUrl || answer.videoPath) {
      // Extract transcript from video
      try {
        const videoTranscriptData = await extractVideoTranscriptFromAnswer(
          answer,
          question.id,
          answer?.userEmail,
          answer?.examCode || 'unknown'
        );
        
        if (videoTranscriptData && videoTranscriptData.transcript) {
          transcript = videoTranscriptData.transcript;
        }
      } catch (error) {
        console.error('Error extracting video transcript:', error);
      }
    }
    
    // Set transcript as answer text for evaluation
    if (transcript) {
      enrichedAnswer.text = transcript;
    } else {
      enrichedAnswer.text = answer.text || answer.answer || '';
    }
    
    // Call evaluateTextWithOpenAI with evaluation points (combines evaluationKeywords and evaluationPoints)
    return await evaluateTextWithOpenAI(question, enrichedAnswer, images, maxScore, getEvaluationPoints(question));
  } else {
    // Default: regular text question (no PDF/video extraction)
    // Call evaluateTextWithOpenAI with evaluation points (combines evaluationKeywords and evaluationPoints)
    return await evaluateTextWithOpenAI(question, answer, images, maxScore, getEvaluationPoints(question));
  }
};

export const analyzeImage = async (imageUrl, questionContext) => {
  return await analyzeImageWithOpenAI(imageUrl, questionContext);
};

// Export the direct evaluation functions for fallback use
export { 
  evaluateVideoScenarioDirect, 
  evaluateScenarioTextDirect, 
  evaluateTextDirect, 
  evaluateCodeDirect 
};

// Main evaluation function for entire submissions
const evaluateSubmission = async (exam, submission) => {
  const evaluations = {};
  let totalScore = submission.provisionalScore ? submission.provisionalScore.score : 0;
  let maxPossibleScore = submission.provisionalScore ? submission.provisionalScore.maxScore : 0;
  
  // Extract user email from submission for logging
  const userEmail = submission.email || 'unknown';

  for (const section of exam.sections) {
    for (const question of section.questions) {
      const answer = submission.answers[question.id];
      if (!answer) continue;
      
      // Add userEmail to answer for logging purposes
      if (!answer.userEmail) {
        answer.userEmail = userEmail;
      }
      
      // Add examCode to answer if not present (needed for video transcription)
      if (!answer.examCode) {
        answer.examCode = submission.examCode || exam.code || 'unknown';
      }
      
      // Get video analysis for video-scenario and video questions
      // Note: For 'video' type questions, transcript extraction is handled in evaluateQuestion function
      let videoAnalysis = null;
      if (question.type === 'video-scenario' || question.type === 'video') {
        // Get videoAnalysis for both video-scenario and video types
        videoAnalysis = submission.videoAnalysis?.[question.id] || null;
      }
      
      if (question.type === 'video-scenario') {
        
        // Extract video transcript during AI evaluation (similar to PDF extraction)
        // Only extract if transcript is not already available from submission
        if (!videoAnalysis || !videoAnalysis.transcript || !videoAnalysis.transcript.trim()) {
          console.log(`🎥 [AI Evaluation] Extracting video transcript for question ${question.id} (similar to PDF extraction)`);
          
          // Extract transcript from video answer during AI evaluation
          // This ensures transcript is available even if not extracted during submission
          const videoTranscriptData = await extractVideoTranscriptFromAnswer(
            answer, 
            question.id, 
            userEmail,
            submission.examCode || 'unknown'
          );
          
          // If we extracted transcript, add/update it in videoAnalysis
          // This ensures the exact transcript (without spell correction) is used for evaluation
          if (videoTranscriptData && videoTranscriptData.transcript) {
            if (!videoAnalysis) {
              videoAnalysis = {
                transcript: videoTranscriptData.transcript, // Exact text without spell correction
                confidence: videoTranscriptData.confidence,
                wordCount: videoTranscriptData.wordCount,
                words: videoTranscriptData.words
              };
            } else {
              // Update existing videoAnalysis with extracted transcript (exact text without spell correction)
              videoAnalysis.transcript = videoTranscriptData.transcript;
              videoAnalysis.confidence = videoTranscriptData.confidence;
              videoAnalysis.wordCount = videoTranscriptData.wordCount;
              videoAnalysis.words = videoTranscriptData.words;
            }
            console.log(`🎥 [AI Evaluation] ✅ Transcript extracted and added to videoAnalysis for question ${question.id} (${videoTranscriptData.wordCount} words)`);
          } else {
            console.log(`🎥 [AI Evaluation] ⚠️ Transcript extraction failed for question ${question.id}`);
          }
        } else {
          // Transcript already exists from submission - use it (exact text without spell correction)
          console.log(`🎥 [AI Evaluation] Using existing transcript for question ${question.id} (${videoAnalysis.wordCount || 0} words)`);
        }
      }
      // Note: For 'video' type questions, transcript extraction is handled in evaluateQuestion function
      
      let evaluationResult;
      const questionMaxScore = question.maxScore || question.marks || 10;
      
      // Use the existing evaluateQuestion function for individual questions
      // Pass videoAnalysis so the exact transcript (without spell correction) is used
      try {
        evaluationResult = await evaluateQuestion(question, answer, videoAnalysis);
      } catch (error) {
        console.error(`Error evaluating question ${question.id}:`, error);
        evaluationResult = {
          score: 0,
          maxScore: questionMaxScore,
          feedback: "Evaluation failed",
          evaluationType: question.type || "unknown",
          confidence: 0
        };
      }
      
      if (evaluationResult) {
        evaluations[question.id] = { 
          ...evaluationResult, 
          maxScore: questionMaxScore
        };
        
        // For MCQs, don't add to totalScore as it's already included in provisionalScore
        if (question.type !== 'mcq-single' && question.type !== 'mcq-multi') {
          totalScore += evaluationResult.score;
          maxPossibleScore += questionMaxScore;
        }
      }
    }
  }

  const overallPercentage = maxPossibleScore > 0 ? Math.round((totalScore / maxPossibleScore) * 100) : 0;

  // Clean the result to remove any undefined values
  const cleanResult = {
    totalScore,
    maxPossibleScore,
    overallPercentage,
    evaluations,
    evaluatedAt: new Date().toISOString(),
  };

  // Recursively clean the evaluations object
  const cleanEvaluations = {};
  for (const [questionId, evaluation] of Object.entries(evaluations)) {
    const cleanEvaluation = {};
    for (const [key, value] of Object.entries(evaluation)) {
      if (value !== undefined) {
        cleanEvaluation[key] = value;
      }
    }
    cleanEvaluations[questionId] = cleanEvaluation;
  }
  cleanResult.evaluations = cleanEvaluations;

  return cleanResult;
};

// Legacy exports for backward compatibility
const AIEvaluationService = {
  evaluateSubmission,
  evaluateQuestion,
  analyzeImage,
  evaluateVideoScenarioDirect,
  evaluateScenarioTextDirect,
  evaluateTextDirect,
  evaluateCodeDirect
};

export default AIEvaluationService;
